import React, { useEffect, useMemo, useState } from 'react';
import { Button, SparkIcon, TagGeneral, Text, TooltipTrigger, sparkToast } from '@spark';
import LLMJudgeConfigModal from '@/features/evaluation/components/Summarization/LLMJudgeConfigModal';
import {
    buildBuiltinCriteriaItems,
    judgeConfigToUsecaseUpdatePayload,
    usecaseToJudgeConfig,
} from '@/features/evaluation/components/Summarization/llmJudgeConfigUtils';
import { updateLlmJudgeConfig } from '@/features/evaluation/api/summarizationApi';

/**
 * Pre-configure LLM-as-a-judge evaluation criteria from the Train page
 * (generative tasks only). Reuses the evaluation page's judge config modal and
 * persists the same `user_criteria_prompts` / `llm_judge_enabled_by_id`
 * usecase fields, so the criteria are already in place when evaluation runs.
 */
const JudgeCriteriaSelector = ({ usecase, onPersisted }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [judgeConfig, setJudgeConfig] = useState(() => usecaseToJudgeConfig(usecase));

    useEffect(() => {
        setJudgeConfig(usecaseToJudgeConfig(usecase));
    }, [usecase]);

    const builtinItems = useMemo(
        () => buildBuiltinCriteriaItems(usecase?.mandatory_criteria_prompts || {}, judgeConfig?.enabledById),
        [usecase?.mandatory_criteria_prompts, judgeConfig?.enabledById],
    );

    const enabledTitles = useMemo(() => {
        const enabled = judgeConfig?.enabledById || {};
        return [...builtinItems, ...(judgeConfig?.custom || [])]
            .filter((item) => enabled[item.id] !== false && item.enabled !== false)
            .map((item) => item.title);
    }, [builtinItems, judgeConfig]);

    const persist = async (next) => {
        setJudgeConfig(next);
        const payload = judgeConfigToUsecaseUpdatePayload(next);
        try {
            await updateLlmJudgeConfig({
                client_id: usecase?.client_id,
                usecase_id: usecase?._id,
                ...payload,
            });
            onPersisted?.(payload);
        } catch (error) {
            console.error('Failed to save judge criteria:', error);
            sparkToast.error('Failed to save evaluation criteria');
        }
    };

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Text variant="base-semibold">LLM as a judge criteria</Text>
                    <TooltipTrigger text="Pre-configure which criteria the LLM judge scores during evaluation. You can pick from the preset list or add custom criteria.">
                        <SparkIcon slug="circle-info" size="small" />
                    </TooltipTrigger>
                </div>
                <Button
                    variant="secondary"
                    size="small"
                    startIcon={<SparkIcon slug="adjust" size="small" />}
                    disableWhenReadOnly
                    onClick={() => setModalOpen(true)}
                >
                    Configure
                </Button>
            </div>
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {enabledTitles.length > 0 ? (
                    enabledTitles.map((title) => <TagGeneral key={title} label={title} />)
                ) : (
                    <Text variant="base-regular" color="secondary">
                        No criteria enabled
                    </Text>
                )}
            </div>

            <LLMJudgeConfigModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                value={{
                    ...judgeConfig,
                    builtins: builtinItems,
                }}
                onChange={persist}
            />
        </div>
    );
};

export default JudgeCriteriaSelector;
